"use client";

import { useEffect, useMemo } from "react";

import Image from "next/image";

import { User } from "lucide-react";

import { getBookingInstate } from "@repo/ui/api/getBookingInstate";
import { useExtrasStatus } from "@repo/ui/components/Extras/common/useExtrasStatus";
import { DIRECTION_LABELS, DIRECTIONS } from "@repo/ui/constants/directions";
import { PASSENGER_TYPES } from "@repo/ui/constants/passengerTypes";
import { SectionScrollIds } from "@repo/ui/constants/scrollIds";
import { SSR_CODES, SSR_CODES_CHECKIN } from "@repo/ui/constants/ssrs";
import { findSSRWithPurchasePriority } from "@repo/ui/helpers/Extras/findPurchasedSsr";
import { getDirection } from "@repo/ui/helpers/Extras/getDirection";
import { isSsrInAnyJourneyInBundle } from "@repo/ui/helpers/Extras/journeySsrUtils";
import { usePassengerName } from "@repo/ui/Hooks/usePassengerName";
import { getTabSessionId } from "@repo/ui/lib/tabSessionId";
import type { PassengerSegment } from "@repo/ui/types/booking";
import type { BoardingPassSection, CommonInfoSection } from "@repo/ui/types/cms";
import type { JourneyWithPassengerSegments } from "@repo/ui/types/extras";
import type { BookingSummary } from "@repo/ui/types/manageBooking";

import { useBookingPassengers, useIsCheckin } from "@repo/store/slices";

import ExtrasBoardingPassOptionCard from "./BoardingPassOptionCard";

interface ExtrasBoardingSectionProps {
  journeys?: JourneyWithPassengerSegments[];
  cmsData?: BoardingPassSection;
  commonInfo?: CommonInfoSection;
  retrieveBooking?: BookingSummary | null;
}

// Map booking passengers order to the aggregated passenger segments
export type PassengerWithJourneys = PassengerSegment & {
  journeys: JourneyWithPassengerSegments[];
  passengerTypeCode?: string;
};

export default function ExtrasBoardingSectionCard({
  journeys,
  cmsData,
  commonInfo,
  retrieveBooking,
}: Readonly<ExtrasBoardingSectionProps>) {
  const isCheckinOrPostBookingFlow = useIsCheckin();
  const checkingStatus = useExtrasStatus();
  const bookingPassengers = useBookingPassengers();
  const getPassengerName = usePassengerName();

  const tabSessionId = getTabSessionId();
  useEffect(() => {
    getBookingInstate(tabSessionId).then((result) => console.log("bookingInstate", result));
  }, [tabSessionId]);

  const boardingPassCode = useMemo<string>(() => {
    return isCheckinOrPostBookingFlow ? SSR_CODES_CHECKIN.BOARDING_PASS : SSR_CODES.BOARDING_PASS;
  }, [isCheckinOrPostBookingFlow]);

  const boardingPassCodes = useMemo<readonly string[]>(() => {
    return isCheckinOrPostBookingFlow
      ? [SSR_CODES_CHECKIN.BOARDING_PASS, SSR_CODES.BOARDING_PASS]
      : [SSR_CODES.BOARDING_PASS];
  }, [isCheckinOrPostBookingFlow]);

  if (!journeys || journeys.length === 0) return null;
  // Build a quick lookup of passenger segments across journeys by passengerKey
  const allPassengersMap = new Map();
  journeys.forEach((journey) => {
    Object.values(journey.passengerSegment).forEach((passenger) => {
      if (!allPassengersMap.has(passenger.passengerKey)) {
        allPassengersMap.set(passenger.passengerKey, {
          ...passenger,
          journeys: [journey],
        });
      } else {
        allPassengersMap.get(passenger.passengerKey).journeys.push(journey);
      }
    });
  });

  // const orderedPassengers: PassengerWithJourneys[] = bookingPassengers
  //   .map((bp: BookingPassenger) => {
  //     const seg = allPassengersMap.get(bp.passengerKey) as
  //       | (PassengerSegment & { journeys: JourneyWithPassengerSegments[] })
  //       | undefined;
  //     if (!seg) return null;
  //     return {
  //       ...seg,
  //       passengerTypeCode: bp.passengerTypeCode, // ADT | CHD | INF,
  //     } as PassengerWithJourneys;
  //   })
  //   .filter((p): p is PassengerWithJourneys => Boolean(p));

  const orderedPassengers: PassengerWithJourneys[] = [];

  for (const bp of bookingPassengers) {
    const seg = allPassengersMap.get(bp.passengerKey);
    if (!seg) continue;

    orderedPassengers.push({
      ...seg,
      passengerTypeCode: bp.passengerTypeCode,
    });
  }

  const typeLabel = (code?: string) => {
    switch ((code || "").toUpperCase()) {
      case PASSENGER_TYPES.ADULT:
        return commonInfo?.adultLabel || "Adult";
      case PASSENGER_TYPES.CHILD:
        return commonInfo?.childLabel || "Child";
      case PASSENGER_TYPES.INFANT:
        return commonInfo?.infantLabel || "Infant";
      default:
        return commonInfo?.passengerLabel || "Passenger";
    }
  };

  const isBoardingPassInAnyJourneyBundle = isSsrInAnyJourneyInBundle(journeys, boardingPassCode);
  return (
    <div
      className="flex flex-col-reverse md:flex-row"
      id={SectionScrollIds.BoardingPass}
      data-testid="extras-boarding-section"
    >
      <div className="flex flex-col w-full p-5 space-y-4 bg-white border rounded-xl">
        <div className="flex items-start gap-4">
          {cmsData?.heading?.icon && (
            <Image
              src={cmsData?.heading?.icon}
              width={50}
              height={50}
              alt="logo"
              data-testid="extras-boarding-section-cms-icon"
            />
          )}
          <div className="flex flex-col space-y-1">
            {cmsData?.heading?.headline && (
              <h3
                className="text-xl font-bold text-brand-blue"
                data-testid="extras-boarding-section-cms-headline-lbl"
              >
                {cmsData?.heading?.headline}
              </h3>
            )}
            {cmsData?.heading?.ctaText && (
              <span
                className="text-sm font-normal text-brand-blue"
                data-testid="extras-boarding-section-cms-cta-lbl"
              >
                {cmsData?.heading?.ctaText}
              </span>
            )}
          </div>
        </div>

        {/* Loop passengers in booking order with type labels */}
        {orderedPassengers.map((passenger, pIndex) => {
          const passengerName = getPassengerName(passenger.passengerKey);

          return (
            <div
              key={passenger.passengerKey}
              className="mb-6"
              data-testid={`${passenger?.passengerKey}-wrapper-section`}
            >
              {/* Passenger info */}
              <div
                className="flex flex-wrap text-sm font-medium text-brand-blue gap-x-2 md:text-base"
                data-testid={`${passenger?.passengerKey}-passenger-info-section`}
              >
                <span
                  className="inline-flex items-center"
                  data-testid={`${passenger?.passengerKey}-passenger-info-lbl`}
                >
                  <User className="w-4 h-4 mr-1 text-brand-blue" />
                  {typeLabel(passenger.passengerTypeCode)}{" "}
                  {commonInfo?.passengerLabel ?? "Pasajero"}
                  {passengerName ? `: ${pIndex + 1} - ${passengerName}` : ` ${pIndex + 1}`}
                </span>
              </div>

              {/* Passenger journeys */}
              <div
                className={`flex flex-wrap gap-4 mt-2 ${journeys.length === 1 ? "justify-center" : ""}`}
              >
                {journeys.map((journey, jIndex) => {
                  const direction = getDirection(jIndex);
                  const directionLabel =
                    direction === DIRECTIONS.OUTBOUND
                      ? commonInfo?.outboundLabel || DIRECTION_LABELS.OUTBOUND
                      : commonInfo?.returnLabel || DIRECTION_LABELS.INBOUND;

                  const isJourneyDisabled =
                    direction === DIRECTIONS.OUTBOUND
                      ? checkingStatus?.isOutboundClose
                      : checkingStatus?.isInboundClose;

                  const passengeSsrs = journey.passengerSegment[passenger.passengerKey]?.ssrs ?? [];

                  const bordingPassSsr = findSSRWithPurchasePriority(
                    passengeSsrs,
                    boardingPassCodes,
                    boardingPassCode,
                  );

                  const price = bordingPassSsr?.price ?? 0;

                  const isIncluded = bordingPassSsr?.inBundle || bordingPassSsr?.isConfirmed;

                  const options = [
                    {
                      id: `opt1:${passenger.passengerKey}-${journey.journeyKey}-${direction}`,
                      title: cmsData?.carryOwnBoardingCardLabel ?? "",
                      isIncluded: true,
                      price: 0,
                      ssrKey: "",
                    },
                    {
                      id: `opt2:${passenger.passengerKey}-${journey.journeyKey}-${direction}`,
                      title: cmsData?.getBoardingCardAtAirportLabel ?? "",
                      isIncluded,
                      inBundle: bordingPassSsr?.inBundle,
                      price: Number(price),
                      ssrKey: bordingPassSsr?.ssrKey,
                    },
                  ];

                  return (
                    <div
                      key={`${passenger.passengerKey}-${journey.journeyKey}-${jIndex}-${pIndex}`}
                      className={
                        journeys.length === 1
                          ? "w-full md:w-1/2 max-w-[600px]"
                          : "w-full flex-[1_1_100%] sm:flex-[1_1_49%]"
                      }
                      data-testid={`${passenger.passengerKey}-${journey.journeyKey}-${jIndex}-${pIndex}-section`}
                    >
                      <ExtrasBoardingPassOptionCard
                        InBundleInAnyJourney={isBoardingPassInAnyJourneyBundle}
                        segmentId={`${passenger.passengerKey}-${journey.journeyKey}`}
                        direction={direction}
                        directionLabel={directionLabel}
                        origin={journey.designator.origin}
                        destination={journey.designator.destination}
                        options={options}
                        bundleCode={journey.bundleCode}
                        passengerKey={passenger.passengerKey}
                        inBundle={isIncluded}
                        commonInfo={commonInfo}
                        passengers={retrieveBooking?.passengers}
                        cmsData={cmsData}
                        isJourneyDisabled={isJourneyDisabled}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {cmsData?.infoText && (
          <div className="flex items-center gap-2 p-2 font-bold rounded-md shadow bg-yellow-50">
            {cmsData?.infoIcon && (
              <Image
                src={cmsData?.infoIcon}
                width={18}
                height={18}
                alt="Warning icon"
                className="shrink-0"
              />
            )}
            <div
              className="[&>p]:m-0 text-sm text-yellow-800"
              dangerouslySetInnerHTML={{ __html: cmsData.infoText }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
